import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { BoxOfferInputCustomLabel, BoxOfferRuleCard } from "@modules/boxOffer/components/boxOfferFormSection"
import { type BoxOfferRule, BoxOfferRulePeriod } from "@modules/boxOffer/entities/boxOfferTypes"
import { boxOfferRuleValidationSchema } from "@modules/boxOffer/types/boxOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { capitalize } from "@modules/utils/stringUtils"
import { PencilIcon, PlusCircleIcon, X } from "lucide-react"
import { type Dispatch, type SetStateAction, type SyntheticEvent, useEffect, useState } from "react"
import styled from "styled-components"

type BoxOfferFormRulesInputProps = {
  rules: BoxOfferRule[]
  onChangeRules: (rules: BoxOfferRule[]) => void
  setDisabled: Dispatch<SetStateAction<boolean>>
}
export const BoxOfferFormRulesInput = ({ rules, onChangeRules, setDisabled }: BoxOfferFormRulesInputProps) => {
  const [displayNewRuleInput, setDisplayNewRuleInput] = useState(false)
  const [ruleToUpdate, setRuleToUpdate] = useState<BoxOfferRule>()

  return (
    <div className="flex flex-col gap-1">
      <BoxOfferInputCustomLabel
        label={word("box.offer.form.infos.restrictedPeriod.title")}
        description={word("box.offer.form.infos.restrictedPeriod.SUBSCRIPTION.details")}
        isRequired={false}
      />

      <div className="flex flex-wrap gap-2">
        {rules.map((rule, index) =>
          ruleToUpdate?.period === rule.period ? (
            <CustomRuleInput
              key={index}
              rules={rules}
              onChangeRules={onChangeRules}
              onClose={() => {
                setRuleToUpdate(undefined)
              }}
              ruleToUpdate={ruleToUpdate}
              setDisabled={setDisabled}
            />
          ) : (
            <BoxOfferRuleCard
              key={index}
              onClick={() => {
                setRuleToUpdate(rule)
              }}
              $pointer
            >
              <Text fontStyle="TS" className="text-center">
                {`${rule.value.toString()} ${word("offer.form.rules.sessions", { count: rule.value })} ${word(
                  `offer.form.rules.period.per.${rule.period}`,
                )}`}
              </Text>
              <PencilIcon className="text-2xl" />
            </BoxOfferRuleCard>
          ),
        )}

        {rules.length < 3 &&
          (!displayNewRuleInput ? (
            <BoxOfferRuleCard
              onClick={() => {
                setDisplayNewRuleInput(true)
              }}
              $new
              $pointer
            >
              <Text fontStyle="TS" className="text-neutral-600">
                {word("box.offer.form.infos.restrictedPeriod.addRule")}
              </Text>
              <PlusCircleIcon className="text-2xl" />
            </BoxOfferRuleCard>
          ) : (
            <CustomRuleInput
              rules={rules}
              onChangeRules={onChangeRules}
              onClose={() => {
                setDisplayNewRuleInput(false)
              }}
              setDisabled={setDisabled}
            />
          ))}
      </div>
    </div>
  )
}

type CustomRuleInputProps = {
  rules: BoxOfferRule[]
  onChangeRules: (rules: BoxOfferRule[]) => void
  onClose: () => void
  ruleToUpdate?: BoxOfferRule
  setDisabled: Dispatch<SetStateAction<boolean>>
}
const CustomRuleInput = ({ rules, onChangeRules, onClose, ruleToUpdate, setDisabled }: CustomRuleInputProps) => {
  const rulesPeriodOptions = Object.values(BoxOfferRulePeriod).map((period) => ({
    value: period,
    label: capitalize(word(`offer.form.rules.period.per.${period}`)),
  }))

  // Disable the page submit button while a rule is being edited
  useEffect(() => {
    setDisabled(true)
    return () => {
      setDisabled(false)
    }
  }, [setDisabled])

  const form = useAppForm({
    defaultValues: {
      value: ruleToUpdate?.value ?? 0,
      period: ruleToUpdate?.period ?? BoxOfferRulePeriod.DAY,
    } satisfies BoxOfferRule,
    validators: {
      onSubmit: boxOfferRuleValidationSchema(rules),
    },
    onSubmit: ({ value: rule }) => {
      const updatedRules = rules.some((existingRule) => existingRule.period === rule.period)
        ? rules.map((existingRule) => (existingRule.period === rule.period ? rule : existingRule))
        : [...rules, rule]
      onChangeRules(updatedRules)
      onClose()
    },
  })

  const handleDelete = () => {
    const period = form.getFieldValue("period")
    if (rules.some((rule) => rule.period === period)) {
      onChangeRules(rules.filter((rule) => rule.period !== period))
    }
    onClose()
  }

  return (
    <BoxOfferRulesContainer>
      <form.Subscribe selector={(state) => state.errors.length > 0}>
        {(hasError) => (
          <Text fontStyle="LS" className={cn("text-neutral-700", { "text-red-500": hasError })}>
            {word("box.offer.form.infos.restrictedPeriod.inputName").toUpperCase()}
          </Text>
        )}
      </form.Subscribe>

      <div className="flex w-full items-start gap-2">
        <form.AppField name="value">
          {(field) => (
            <div className="flex w-5/12 flex-col gap-1">
              <Input
                id={field.name}
                name={field.name}
                type="number"
                inputMode="numeric"
                step={1}
                min={1}
                max={100}
                placeholder={word("offer.form.numberOfSessions.title")}
                value={field.state.value || ""}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value)
                  field.handleChange(Math.min(100, Math.max(1, isNaN(parsed) ? 0 : parsed)))
                }}
                onBeforeInput={(e: SyntheticEvent<HTMLInputElement>) => {
                  if ("data" in e && isNaN(Number(e.data))) {
                    e.preventDefault()
                  }
                }}
                aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
              />
              <field.Message />
            </div>
          )}
        </form.AppField>

        <form.AppField name="period">
          {(field) => (
            <div className="w-5/12">
              <field.Select items={rulesPeriodOptions}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rulesPeriodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </field.Select>
            </div>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => rules.some((rule) => rule.period === state.values.period)}>
          {(exists) => (
            <Button variant="outline" size="iconMd" onClick={handleDelete}>
              {exists ? <TrashIcon color="currentColor" width={14} height={14} /> : <X className="currentColor" />}
            </Button>
          )}
        </form.Subscribe>
        <Button variant="primary" size="iconMd" onClick={() => void form.handleSubmit()}>
          <CheckmarkIcon color="currentColor" width={14} height={14} />
        </Button>
      </div>
    </BoxOfferRulesContainer>
  )
}

const BoxOfferRulesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
  padding: 1.25rem;
  border-radius: 0.75rem;
  margin-bottom: 1.75rem;
  border: 1px solid var(--borderDefault);
`
